import config from '../../config';
import { getValidToken } from '../../services/tokenRefreshService';

export const VEND_ORGS = ['deeliver', 'vaperite'] as const;
export type VendOrg = (typeof VEND_ORGS)[number];

export interface AddressSuggestion {
  label: string;
  /** A Google suggestion: its fields and pin are fetched with getAddressDetails when picked. */
  placeId?: string | null;
  buildingName: string;
  streetAddress: string;
  suburb: string;
  city: string;
  province: string;
  postalCode: string;
  lat: number | null;
  lng: number | null;
}

export interface StoreLocationView {
  storeId: string;
  name: string;
  isActive: boolean;
  onboarded: boolean;
  geofence: { tag: string; externalId: string; type: string } | null;
  location: {
    buildingName: string;
    streetAddress: string;
    suburb: string;
    city: string;
    province: string;
    postalCode: string;
    lat: number | null;
    lng: number | null;
  };
  routing: { vendOutletId: string; loopBranchId: string; vendOrg: string };
}

export interface StoreLocationInput {
  buildingName: string;
  streetAddress: string;
  suburb: string;
  city: string;
  province: string;
  postalCode: string;
  lat: number;
  lng: number;
  vendOutletId: string;
  loopBranchId: string;
  vendOrg: VendOrg;
  makeLive?: boolean;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getValidToken();
  const res = await fetch(`${config.API_URL}${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.message || `Something went wrong (HTTP ${res.status})`);
  return json as T;
}

export interface AddressSearchOptions {
  /** One per search: ties the typing and the pick into a single billed Google session. */
  sessionToken?: string;
  /** Rank results near here (the store's current pin). */
  near?: { lat: number; lng: number } | null;
}

export const searchAddresses = (q: string, { sessionToken, near }: AddressSearchOptions = {}) => {
  const qs = new URLSearchParams({ q });
  if (sessionToken) qs.set('sessionToken', sessionToken);
  if (near) {
    qs.set('nearLat', String(near.lat));
    qs.set('nearLng', String(near.lng));
  }
  return request<AddressSuggestion[]>(`/admin/stores/address-search?${qs}`);
};

export const getAddressDetails = (placeId: string, sessionToken?: string) => {
  const qs = new URLSearchParams({ placeId });
  if (sessionToken) qs.set('sessionToken', sessionToken);
  return request<AddressSuggestion>(`/admin/stores/address-details?${qs}`);
};

export const getStoreLocation = (storeId: string) =>
  request<StoreLocationView>(`/admin/stores/${encodeURIComponent(storeId)}/location`);

export const saveStoreLocation = (storeId: string, body: StoreLocationInput) =>
  request<StoreLocationView>(`/admin/stores/${encodeURIComponent(storeId)}/location`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
